import React, { useState } from 'react';
import { Box, Typography, Button, IconButton, Select, MenuItem, TextField, RadioGroup, FormControlLabel, Radio, Chip, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useFormContext } from '../context/FormContext';
import { fileToBase64 } from '../utils/fileUtils';
import {
    ROOT_NUMBER_OPTIONS, ROOT_DEVELOPMENT_OPTIONS, ROOT_SHAPE_OPTIONS,
    ROOT_RELATION_OPTIONS, ROOT_CURVATURE_OPTIONS, PERIRADICULAR_BONE_OPTIONS,
    PDL_SPACE_OPTIONS, PERIAPICAL_AREA_OPTIONS, ROOT_NAME_OPTIONS
} from './ImpactionConstants';

import MmInput from './MmInput';

import {
    generateRootMorphologyText,
    generateRootPathologyText,
    generateDynamicRootText  // We will create this
} from '../utils/impactionTextGenerator';

const TextPreviewBox = ({ text }) => {
    if (!text) return null;
    return (
        <Box sx={{ mt: 2, p: 1.5, bgcolor: '#e3f2fd', borderLeft: '4px solid #2196f3', borderRadius: '0 4px 4px 0' }}>
            <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#1565c0' }}>
                {text}
            </Typography>
        </Box>
    );
};

// Helper for consistent label styling
const FieldLabel = ({ children }) => (
    <Typography sx={{ fontSize: '13px', mb: '5px' }}>
        {children}
    </Typography>
);

const ImpactionTab_Roots = ({ selectedTooth }) => {
    const { state, actions } = useFormContext();
    const toothData = state.impactionData?.[selectedTooth] || {};
    const rootsData = toothData.roots || {};
    const rootsList = rootsData.rootsList || [];

    // Helper to update main rootsData
    const updateData = (field, value) => {
        actions.setImpactionData({
            ...state.impactionData,
            [selectedTooth]: {
                ...toothData,
                roots: {
                    ...rootsData,
                    [field]: value
                }
            }
        });
    };

    // Helper to update a specific dynamic root
    const updateRootItem = (index, field, value) => {
        const newList = [...rootsList];
        newList[index] = { ...newList[index], [field]: value };
        updateData('rootsList', newList);
    };

    const addRoot = (rootName) => {
        const newList = [...rootsList, { name: rootName, development: '', shape: [], curvature: '', length: '', image: null }];
        updateData('rootsList', newList);
        setAddRootDialogOpen(false);
    };

    const removeRoot = (index) => {
        const newList = rootsList.filter((_, i) => i !== index);
        updateData('rootsList', newList);
    };

    const [addRootDialogOpen, setAddRootDialogOpen] = useState(false);
    const [selectedRootName, setSelectedRootName] = useState(ROOT_NAME_OPTIONS[0]);

    // Text Generation
    const genText = generateRootMorphologyText(rootsData);
    const genPathText = generateRootPathologyText(rootsData);
    const dynamicRootsText = generateDynamicRootText ? generateDynamicRootText(rootsData) : ''; // Safe check

    const handleImageUpload = async (key) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.onchange = async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;

            try {
                const base64 = await fileToBase64(file);
                const fileData = { base64, name: file.name, type: file.type };
                updateData(key, fileData);
            } catch (error) {
                console.error("Error converting file to Base64:", error);
            }
        };
        input.click();
    };

    const handleRootImageUpload = async (index) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.onchange = async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            try {
                const base64 = await fileToBase64(file);
                const fileData = { base64, name: file.name, type: file.type };
                updateRootItem(index, 'image', fileData);
            } catch (error) {
                console.error("Error converting file to Base64:", error);
            }
        };
        input.click();
    };

    const handleRemoveImage = (key) => {
        updateData(key, null);
    };

    const handleRemoveRootImage = (index) => {
        updateRootItem(index, 'image', null);
    };

    const renderImageSection = (label, imageValue, onUpload, onRemove, height = '250px') => {
        const preview = imageValue ? imageValue.base64 : null;
        return (
            <Box sx={{ width: '250px', flexShrink: 0 }}>
                <Box sx={{ position: 'relative', mb: '15px' }}>
                    <Box
                        sx={{
                            width: '250px',
                            height: height,
                            border: '2px solid black',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'grey.200',
                            position: 'relative',
                            overflow: 'hidden'
                        }}
                    >
                        {preview && (
                            <IconButton
                                size="small"
                                color="error"
                                onClick={onRemove}
                                sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'background.paper', zIndex: 1 }}
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        )}
                        {preview ? (
                            <img src={preview} alt={label} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                        ) : (
                            <Typography variant="body2" color="text.secondary">{label}</Typography>
                        )}
                    </Box>
                    {imageValue && (
                        <Typography variant="caption" sx={{ color: 'success.main', fontSize: 10, display: 'block', mt: 0.5 }}>
                            {imageValue.name}
                        </Typography>
                    )}
                </Box>
                <Button
                    variant={preview ? "outlined" : "contained"}
                    fullWidth
                    size="small"
                    color={preview ? "secondary" : "primary"}
                    onClick={onUpload}
                    sx={{ mb: '15px' }}
                >
                    {preview ? 'Replace' : 'Select'}
                </Button>
            </Box>
        );
    };

    const sectionBoxStyle = { border: '1px solid #e0e0e0', borderRadius: '4px', mb: '20px', p: '20px' };
    const sectionHeaderStyle = { fontSize: '18px', fontWeight: 'bold', mb: '15px', pb: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' };
    const inputStyle = { '& .MuiOutlinedInput-root': { borderRadius: 0 }, '& .MuiOutlinedInput-input': { padding: '8px 14px', fontSize: '14px' } };

    return (
        <Box>
            <Typography variant="h6" gutterBottom color="primary">
                Roots Assessment ({selectedTooth})
            </Typography>

            {/* SECTION 1: GENERAL ROOT INFO */}
            <Box sx={sectionBoxStyle}>
                <Typography sx={sectionHeaderStyle}>
                    GENERAL ROOT INFO
                </Typography>
                <Box sx={{ display: 'flex', gap: '25px', flexDirection: { xs: 'column', lg: 'row' } }}>
                    {renderImageSection('Axial Root Image', rootsData.axialImage, () => handleImageUpload('axialImage'), () => handleRemoveImage('axialImage'))}

                    <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', alignContent: 'start' }}>

                        {/* Number of Roots + Description */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gridColumn: 'span 2' }}>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <Box sx={{ flex: 1 }}>
                                    <FieldLabel>Number of Roots</FieldLabel>
                                    <Select
                                        value={rootsData.number || ''}
                                        onChange={(e) => updateData('number', e.target.value)}
                                        displayEmpty
                                        size="small"
                                        fullWidth
                                        sx={inputStyle}
                                    >
                                        {ROOT_NUMBER_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                                    </Select>
                                </Box>
                                <Box sx={{ flex: 2 }}>
                                    <FieldLabel>Note / Description (Optional)</FieldLabel>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        placeholder="e.g. Names of roots"
                                        value={rootsData.rootNumberDescription || ''}
                                        onChange={(e) => updateData('rootNumberDescription', e.target.value)}
                                        sx={inputStyle}
                                    />
                                </Box>
                            </Box>
                        </Box>

                        {/* Relation + Description */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gridColumn: 'span 2' }}>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <Box sx={{ flex: 1 }}>
                                    <FieldLabel>Relation between Roots</FieldLabel>
                                    <Select
                                        value={rootsData.relation || ''}
                                        onChange={(e) => updateData('relation', e.target.value)}
                                        displayEmpty
                                        size="small"
                                        fullWidth
                                        sx={inputStyle}
                                    >
                                        {ROOT_RELATION_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                                    </Select>
                                </Box>
                                <Box sx={{ flex: 2 }}>
                                    <FieldLabel>Additional Finding / Description</FieldLabel>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        placeholder="Add description"
                                        value={rootsData.rootRelationDescription || ''}
                                        onChange={(e) => updateData('rootRelationDescription', e.target.value)}
                                        sx={inputStyle}
                                    />
                                </Box>
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <FieldLabel>Periradicular Bone</FieldLabel>
                            <Select
                                value={rootsData.periradicular || ''}
                                onChange={(e) => updateData('periradicular', e.target.value)}
                                displayEmpty
                                size="small"
                                sx={inputStyle}
                            >
                                {PERIRADICULAR_BONE_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                            </Select>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <FieldLabel>PDL Space</FieldLabel>
                            <Select
                                value={rootsData.pdl || ''}
                                onChange={(e) => updateData('pdl', e.target.value)}
                                displayEmpty
                                size="small"
                                sx={inputStyle}
                            >
                                {PDL_SPACE_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                            </Select>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <FieldLabel>Periapical Area</FieldLabel>
                            <Select
                                value={rootsData.periapical || ''}
                                onChange={(e) => updateData('periapical', e.target.value)}
                                displayEmpty
                                size="small"
                                sx={inputStyle}
                            >
                                {PERIAPICAL_AREA_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                            </Select>
                        </Box>
                    </Box>
                </Box>
                <TextPreviewBox text={genText + (genPathText ? " " + genPathText : "")} />
            </Box>

            {/* DYNAMIC ROOT SECTIONS */}
            {rootsList.map((rootItem, index) => (
                <Box key={index} sx={sectionBoxStyle}>
                    <Box sx={sectionHeaderStyle}>
                        <Typography sx={{ fontWeight: 'bold' }}>{rootItem.name.toUpperCase()} DETAILS</Typography>
                        <IconButton size="small" color="error" onClick={() => removeRoot(index)}>
                            <DeleteIcon />
                        </IconButton>
                    </Box>
                    <Box sx={{ display: 'flex', gap: '25px', flexDirection: { xs: 'column', lg: 'row' } }}>
                        {renderImageSection(`${rootItem.name} Image`, rootItem.image, () => handleRootImageUpload(index), () => handleRemoveRootImage(index))}

                        <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', alignContent: 'start' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                <FieldLabel>Development</FieldLabel>
                                <Select
                                    value={rootItem.development || ''}
                                    onChange={(e) => updateRootItem(index, 'development', e.target.value)}
                                    displayEmpty
                                    size="small"
                                    sx={inputStyle}
                                >
                                    {ROOT_DEVELOPMENT_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                                </Select>
                            </Box>

                            {/* Shape - MULTI SELECT */}
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                <FieldLabel>Shape (Multi-select)</FieldLabel>
                                <Select
                                    multiple
                                    value={rootItem.shape || []} // ensure array
                                    onChange={(e) => updateRootItem(index, 'shape', typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                                    displayEmpty
                                    size="small"
                                    sx={inputStyle}
                                    renderValue={(selected) => (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {(selected || []).map((value) => (
                                                <Chip key={value} label={value} size="small" />
                                            ))}
                                        </Box>
                                    )}
                                >
                                    {ROOT_SHAPE_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                                </Select>
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                <FieldLabel>Curvature</FieldLabel>
                                <Select
                                    value={rootItem.curvature || ''}
                                    onChange={(e) => updateRootItem(index, 'curvature', e.target.value)}
                                    displayEmpty
                                    size="small"
                                    sx={inputStyle}
                                >
                                    {ROOT_CURVATURE_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                                </Select>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                <FieldLabel>Length (mm)</FieldLabel>
                                <TextField
                                    size="small"
                                    type="number"
                                    value={rootItem.length || ''}
                                    onChange={(e) => updateRootItem(index, 'length', e.target.value)}
                                    sx={inputStyle}
                                />
                            </Box>
                            {/* WIDTH REMOVED as requested */}
                        </Box>
                    </Box>
                    {/* Add specific text preview for this root if needed, or rely on main one */}
                </Box>
            ))}

            <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setAddRootDialogOpen(true)}
                sx={{ mb: 4, width: '100%', borderStyle: 'dashed', py: 2 }}
            >
                Add Root Section
            </Button>

            <TextPreviewBox text={dynamicRootsText} />

            {/* ADD ROOT DIALOG */}
            <Dialog open={addRootDialogOpen} onClose={() => setAddRootDialogOpen(false)}>
                <DialogTitle>Add New Root Section</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 1, minWidth: '300px' }}>
                        <FieldLabel>Select Root Name</FieldLabel>
                        <Select
                            value={selectedRootName}
                            onChange={(e) => setSelectedRootName(e.target.value)}
                            fullWidth
                            size="small"
                        >
                            {ROOT_NAME_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                        </Select>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddRootDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={() => addRoot(selectedRootName)}>Add</Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
};

export default ImpactionTab_Roots;
